import { Info, LoaderCircle, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  apiClient,
  type PromptDefault,
  type PromptDefaultCheck,
  type PromptDefaultInput
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { promptTaskLabel } from "@/shared/vocabulary/prompt-tasks";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { formatShortDate } from "@/shared/utils/format";
import { promptMarkers } from "@/shared/text/prompt-markers";
import { useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";

/**
 * Administração → Prompts padrão.
 *
 * O conjunto de 13 prompts (tarefa × fonte × posição) que TODA empresa e
 * todo concorrente segue (2026-10-27). É cadastro da INSTALAÇÃO: editar
 * aqui vale para quem segue a linha, a partir do próximo upload; a empresa
 * que personalizou uma linha (Ajustes → Configuração de prompt) não muda.
 * Não há criar nem apagar: as tarefas indexam por posição.
 *
 * O servidor valida como valida a edição por empresa (marcadores,
 * regex, número de grupos de captura); o erro dele aparece na linha.
 * Antes de gravar, a tela pergunta (`check`) quantas empresas que seguem
 * a linha ficariam sem classificar — marcador de premissa obrigatória sem
 * premissa cadastrada — e pede confirmação se houver alguma.
 */

const SOURCE_LABELS: Record<string, string> = { press: "Imprensa", network: "Rede" };

type Draft = {
  header: string;
  extra_header: string;
  output_template: string;
  temperature: string;
  system_message: string;
};

function draftOf(row: PromptDefault): Draft {
  return {
    header: row.header,
    extra_header: row.extra_header ?? "",
    output_template: row.output_template,
    temperature: String(row.temperature),
    system_message: row.system_message ?? ""
  };
}

/**
 * A temperatura digitada como número, ou `null` se não há número.
 *
 * `Number("")` é 0: um campo apagado virava "determinístico" e era
 * gravado sem ninguém ter digitado 0 — o mesmo colapso vazio/zero que o
 * `fav_neutral` do CENA precisou de rádio pra evitar (2026-10-09, teste
 * de render). Vazio ou não numérico é INVÁLIDO, não zero.
 */
export function parseTemperature(text: string): number | null {
  if (text.trim() === "") return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Texto vazio é "sem valor" nos dois campos opcionais, como no banco. */
export function payloadOf(draft: Draft): PromptDefaultInput {
  return {
    header: draft.header,
    extra_header: draft.extra_header === "" ? null : draft.extra_header,
    output_template: draft.output_template,
    // O `?? 0` é só pro tipo: quem grava recusa antes (`save` não roda e o
    // botão fica apagado com temperatura inválida), então o zero nunca sai.
    temperature: parseTemperature(draft.temperature) ?? 0,
    system_message: draft.system_message === "" ? null : draft.system_message
  };
}

/** Mudou algo em relação ao que está salvo? É o que habilita o botão. */
export function isDirty(row: PromptDefault, draft: Draft): boolean {
  const saved = draftOf(row);
  return (Object.keys(saved) as (keyof Draft)[]).some(key => {
    if (key === "temperature") return parseTemperature(draft.temperature) !== row.temperature;
    return draft[key] !== saved[key];
  });
}

/** Quantas empresas a lista da confirmação nomeia; o resto vira "e mais N". */
const BLOCKED_LISTED = 8;

/** "A (positivo); B (neutro) e mais N" — as primeiras empresas pelo nome. */
function listedCompanies(items: PromptDefaultCheck["newly_blocked"]): string {
  const listed = items
    .slice(0, BLOCKED_LISTED)
    .map(item => `${item.company_name} (${item.placeholders.join(", ")})`)
    .join("; ");
  const rest = items.length - BLOCKED_LISTED;
  return `${listed}${rest > 0 ? ` e mais ${rest}` : ""}`;
}

/** O texto da confirmação: quem PASSARIA a parar por causa da edição e por
 *  quê. Quem já estava parado entra só numa frase no fim, porque não é a
 *  edição que o para (medido no RDS em 2026-10-27: 5 de 30 empresas
 *  apareciam em qualquer edição de polaridade). */
export function blockedSummary(check: PromptDefaultCheck): string {
  const count = check.newly_blocked.length;
  const already = check.already_blocked.length;
  return (
    `${count === 1 ? "1 empresa que classifica hoje pararia" : `${count} empresas que classificam hoje parariam`} ` +
    `com este texto: ele usa marcador de premissa que elas não cadastraram. ` +
    `${listedCompanies(check.newly_blocked)}. ` +
    `Ao todo ${check.following === 1 ? "1 empresa segue" : `${check.following} empresas seguem`} esta linha.` +
    (already > 0
      ? ` Além delas, ${already === 1 ? "1 já não classifica" : `${already} já não classificam`} hoje e continuaria${already === 1 ? "" : "m"} assim.`
      : "")
  );
}

/** A nota depois de gravar: quem segue parado (não foi a edição) e quem
 *  voltou a classificar. Vazia quando não há nada a dizer. */
export function stillStoppedNote(check: PromptDefaultCheck): string | null {
  const parts: string[] = [];
  const already = check.already_blocked.length;
  if (already > 0) {
    parts.push(
      `${already === 1 ? "1 empresa já não classificava" : `${already} empresas já não classificavam`} ` +
        `por falta de premissa e ${already === 1 ? "continua parada" : "continuam paradas"}: ` +
        `${listedCompanies(check.already_blocked)}.`
    );
  }
  if (check.unblocked > 0) {
    parts.push(
      check.unblocked === 1
        ? "1 empresa volta a classificar com este texto."
        : `${check.unblocked} empresas voltam a classificar com este texto.`
    );
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

type PromptGroup = {
  key: string;
  title: string;
  task: string;
  source: string;
  rows: PromptDefault[];
};

/** Agrupa por tarefa e fonte, na ordem em que o servidor mandou
 *  (já vem por tarefa, fonte e posição). */
export function groupRows(
  rows: PromptDefault[]
): PromptGroup[] {
  const groups = new Map<string, PromptGroup>();
  for (const row of rows) {
    const key = `${row.task}|${row.source}`;
    const task = promptTaskLabel(row.task);
    const source = SOURCE_LABELS[row.source] ?? row.source;
    const group = groups.get(key) ?? { key, title: `${task} — ${source}`, task, source, rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }
  return [...groups.values()];
}

export { promptMarkers };

export function PromptDefaultsAdmin({ token }: { token: string }) {
  const [rows, setRows] = useState<PromptDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Quais prompts têm mudança não salva: a lista marca, e trocar de prompt
  // não perde o que foi digitado (os editores ficam montados, só escondidos).
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<number>>(new Set());

  const load = useCallback(async () => {
    try {
      setRows(await apiClient.listPromptDefaults(token));
      setError(null);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível carregar os prompts padrão."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const markDirty = useCallback((id: number, dirty: boolean) => {
    setDirtyIds(current => {
      if (current.has(id) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  if (loading) return <ApiState kind="loading">Carregando prompts padrão…</ApiState>;

  const groups = groupRows(rows);
  const selected = rows.find(row => row.id === selectedId) ?? rows[0] ?? null;

  return (
    <div className="copiloto-admin ajustes-panel admin-panel">
      <AdminPageHeader
        title="Prompts padrão"
        description="O conjunto de prompts que toda empresa segue, uma linha por tarefa, fonte e posição."
      />

      <p className="cena-notice">
        <Info size={14} aria-hidden="true" /> Vale para todas as empresas que seguem o geral, a
        partir do próximo upload. As linhas personalizadas por uma empresa não mudam.
      </p>

      {error && <p className="form-error" role="alert">{error}</p>}

      {selected && (
        <div className="prompt-defaults-layout">
          {/* A lista: um item por prompt, agrupado por tarefa e fonte */}
          <nav className="card prompt-defaults-list" aria-label="Prompts padrão">
            {groups.map(group => (
              <div key={group.key} className="prompt-defaults-group">
                <h3>{group.title}</h3>
                {group.rows.map(row => {
                  const active = row.id === selected.id;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className={active ? "active" : ""}
                      aria-current={active ? "true" : undefined}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <span>Posição {row.position}</span>
                      {dirtyIds.has(row.id) && (
                        <span className="prompt-defaults-dirty" title="Alteração não salva">
                          Não salvo
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* O editor: todos montados (o rascunho de cada um sobrevive à
              troca), só o escolhido aparece */}
          <div className="prompt-defaults-editors">
            {groups.map(group =>
              group.rows.map(row => (
                <div key={row.id} hidden={row.id !== selected.id}>
                  <PromptDefaultEditor
                    token={token}
                    row={row}
                    title={group.title}
                    onDirtyChange={markDirty}
                    onSaved={saved =>
                      setRows(current => current.map(r => (r.id === saved.id ? saved : r)))
                    }
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function PromptDefaultEditor({
  token,
  row,
  title,
  onDirtyChange,
  onSaved
}: {
  token: string;
  row: PromptDefault;
  title: string;
  onDirtyChange: (id: number, dirty: boolean) => void;
  onSaved: (saved: PromptDefault) => void;
}) {
  const [draft, setDraft] = useState<Draft>(draftOf(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // O resultado do `check` quando há empresa que pararia de classificar:
  // enquanto existe, a confirmação está aberta e nada foi gravado.
  const [pendingCheck, setPendingCheck] = useState<PromptDefaultCheck | null>(null);
  // Depois de gravar: quem segue parado sem ser por causa da edição.
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const dirty = isDirty(row, draft);
  const temperature = parseTemperature(draft.temperature);
  const id = `prompt-default-${row.id}`;
  const markers = promptMarkers(`${draft.header}\n${draft.extra_header}`);
  const editedBy = row.updated_by && row.updated_by !== "seed"
    ? row.updated_by_name || row.updated_by
    : null;
  // O resumo do que está recolhido: sem abrir, a pessoa vê se há algo ali.
  const advancedSummary = [
    `temperatura ${draft.temperature.trim() === "" ? "—" : draft.temperature.replace(".", ",")}`,
    draft.system_message ? "com mensagem de sistema" : null,
    draft.extra_header ? "com cabeçalho extra" : null
  ].filter(Boolean).join(" · ");

  useEffect(() => {
    onDirtyChange(row.id, dirty);
  }, [onDirtyChange, row.id, dirty]);

  const write = async (check: PromptDefaultCheck): Promise<boolean> => {
    const saved = await apiClient.updatePromptDefault(token, row.id, payloadOf(draft));
    onSaved(saved);
    setDraft(draftOf(saved));
    setSavedNote(stillStoppedNote(check));
    return true;
  };

  /** Mede antes de gravar. `check` que falha (422, 429, rede) NÃO grava; com
   * empresa que PASSARIA a parar de classificar, abre a confirmação e
   * devolve `false` (o "salvar e sair" do aviso de saída fica na tela).
   * Quem já estava parado não pede confirmação: vira nota depois. */
  const save = async (): Promise<boolean> => {
    if (temperature === null) return false;
    setSaving(true);
    setError(null);
    setSavedNote(null);
    try {
      const check = await apiClient.checkPromptDefault(token, row.id, payloadOf(draft));
      if (check.newly_blocked.length > 0) {
        setPendingCheck(check);
        return false;
      }
      return await write(check);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível salvar o prompt."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = async (check: PromptDefaultCheck) => {
    setPendingCheck(null);
    setSaving(true);
    setError(null);
    try {
      await write(check);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível salvar o prompt."));
    } finally {
      setSaving(false);
    }
  };
  // Sair da tela com este prompt alterado pergunta antes; os editores
  // ficam todos montados, então cada um responde pelo seu.
  useUnsavedChanges(dirty, save, `${title} · posição ${row.position}`);

  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  return (
    <section className={`card prompt-default-row${dirty ? " is-dirty" : ""}`} aria-label={`${title} · Posição ${row.position}`}>
      <div className="prompt-default-head">
        <div>
          <h3>{title}</h3>
          <p>
            Posição {row.position}
            {editedBy && (
              <>
                {" · editado por "}
                {editedBy}
                {row.updated_at ? ` em ${formatShortDate(new Date(row.updated_at))}` : ""}
              </>
            )}
            {row.overridden_by > 0 && (
              <>
                {" · "}
                {row.overridden_by === 1
                  ? "1 empresa personalizou esta linha"
                  : `${row.overridden_by} empresas personalizaram esta linha`}
              </>
            )}
          </p>
        </div>
        {dirty && <span className="badge-pill warning">Alterações não salvas</span>}
      </div>

      <div className="admin-field-group">
        <label className="field" htmlFor={`${id}-header`}>
          <span>Texto do prompt</span>
          <textarea
            id={`${id}-header`}
            className="prompt-code"
            rows={12}
            spellCheck={false}
            value={draft.header}
            onChange={event => set({ header: event.target.value })}
          />
        </label>
        {markers.length > 0 && (
          <p className="prompt-markers">
            <span>Preenchido na hora com:</span>
            {markers.map(marker => (
              <code key={marker}>{`$$$${marker}$$$`}</code>
            ))}
          </p>
        )}
      </div>

      <details className="prompt-default-advanced">
        <summary>
          <span>Configurações avançadas</span>
          <small>{advancedSummary}</small>
        </summary>
        <div className="prompt-default-advanced-grid">
          <div className="admin-field-group">
            <label className="field" htmlFor={`${id}-system`}>
              <span>Mensagem de sistema</span>
              <textarea
                id={`${id}-system`}
                rows={3}
                value={draft.system_message}
                onChange={event => set({ system_message: event.target.value })}
              />
            </label>
            <small className="admin-field-help">
              O papel que a IA assume antes de ler o prompt. Vazio = sem mensagem.
            </small>
          </div>
          <div className="admin-field-group">
            <label className="field" htmlFor={`${id}-extra`}>
              <span>Cabeçalho extra</span>
              <textarea
                id={`${id}-extra`}
                rows={3}
                value={draft.extra_header}
                onChange={event => set({ extra_header: event.target.value })}
              />
            </label>
            <small className="admin-field-help">
              Texto somado ao prompt. Vazio = nada a somar.
            </small>
          </div>
          <div className="admin-field-group">
            <label className="field" htmlFor={`${id}-output`}>
              <span>Formato da resposta (regex)</span>
              <textarea
                id={`${id}-output`}
                className="prompt-code"
                rows={2}
                spellCheck={false}
                value={draft.output_template}
                onChange={event => set({ output_template: event.target.value })}
              />
            </label>
            <small className="admin-field-help">
              Como ler a resposta da IA; cada grupo entre parênteses vira um valor.
            </small>
          </div>
          <div className="admin-field-group">
            <label className="field" htmlFor={`${id}-temperature`}>
              <span>Temperatura</span>
              <input
                id={`${id}-temperature`}
                className="prompt-temperature"
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={draft.temperature}
                onChange={event => set({ temperature: event.target.value })}
              />
            </label>
            <small className="admin-field-help">
              De 0 a 2. Perto de 0 a resposta é mais previsível; maior, mais variada.
            </small>
          </div>
        </div>
      </details>

      {temperature === null && (
        <p className="form-error">Informe a temperatura (um número de 0 a 2).</p>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
      {savedNote && <p className="field-warning">{savedNote}</p>}
      <div className="prompt-default-actions">
        <button
          className="primary-button"
          type="button"
          disabled={!dirty || temperature === null || saving}
          onClick={() => void save()}
        >
          {saving ? <LoaderCircle className="spin" size={16} /> : <Save size={16} />} Salvar
        </button>
      </div>
      {pendingCheck && (
        <ConfirmDialog
          title="Gravar mesmo assim?"
          description={blockedSummary(pendingCheck)}
          confirmLabel="Gravar mesmo assim"
          danger
          onCancel={() => setPendingCheck(null)}
          onConfirm={() => void confirmSave(pendingCheck)}
        />
      )}
    </section>
  );
}
